import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ws } from "@/lib/ws";
import { FEATURE_FLAG_DEFS } from "@shared/protocol";
import type { FeatureFlagMeta } from "@shared/protocol";
import { relativeTime } from "@/lib/utils";

/** Super-admin-only. The set of flags is fixed in code (FEATURE_FLAG_DEFS)
 * — this page only ever toggles one on/off, it can't create new ones. */
export function FeatureFlagsPage() {
  const [flags, setFlags] = useState<FeatureFlagMeta[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const offState = ws.on("flags.state", (m) => setFlags(m.flags));
    const offErr = ws.on("error", (m) => {
      if (m.req_type.startsWith("flags.")) setError(m.message);
    });
    ws.send({ type: "flags.list" });
    return () => {
      offState();
      offErr();
    };
  }, []);

  function toggle(key: FeatureFlagMeta["key"], enabled: boolean) {
    ws.send({ type: "flags.set", key, enabled });
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Feature Flags</h1>
        <p className="text-sm text-muted-foreground">
          Toggle platform-wide features on or off. A super_admin can always see
          gated pages regardless of a flag's state, to preview or test before
          shipping.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Flags</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Flag</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last changed</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {FEATURE_FLAG_DEFS.map((def) => {
                const state = flags.find((f) => f.key === def.key);
                const enabled = state?.enabled ?? false;
                return (
                  <TableRow key={def.key}>
                    <TableCell className="font-medium">{def.label}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-md">
                      {def.description}
                    </TableCell>
                    <TableCell>
                      {enabled ? (
                        <Badge variant="success">on</Badge>
                      ) : (
                        <Badge variant="outline">off</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {state?.updated_at ? relativeTime(state.updated_at) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggle(def.key, !enabled)}
                      >
                        Turn {enabled ? "off" : "on"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
